import { useState } from 'react'
import { Control, Controller, FieldErrors } from 'react-hook-form'

import { useLazyFetchFieldSuggestion } from '@entities/User/api/fieldSuggestion.gql'

import ErrorMessage from '@shared/ui/error-message'
import { FormItem } from '@shared/ui/form'
import Input from '@shared/ui/input'
import InputDate from '@shared/ui/input-date'
import MultiSelect from '@shared/ui/multi-select/MultiSelect'
import Select from '@shared/ui/select'


import AutocompleteInput from './AutocompleteInput'
import { ProfileInfoForm } from './info'

import styles from './styles.module.scss'

import type { FormField as FormFieldType } from '@@types'

interface FormFieldProps {
  field: FormFieldType
  control: Control<ProfileInfoForm>
  errors: FieldErrors<ProfileInfoForm>
  disabled?: boolean
  isCorrectEmail?: boolean
  originalEmail?: string | null
}

export default function FormField ({
  field,
  control,
  errors,
  disabled = false,
  isCorrectEmail = true,
  originalEmail,
}: FormFieldProps) {
  const [ , setFocused ] = useState(false)
  const [ suggestions, setSuggestions ] = useState<string[]>([])
  const [ fetchSuggestions ] = useLazyFetchFieldSuggestion()

  if (!field?.fieldName) return null

  const fieldError = errors.profileInfo?.[field.fieldName]
  const fieldValue = control._formValues?.profileInfo?.[field.fieldName]

  const handleGetSuggestions = async (query: string) => {
    if (!field.isAutoComplete || query.length < 2) {
      setSuggestions([])
      return
    }

    const result = await fetchSuggestions({
      variables: {
        fieldName: field.fieldName.split('__').join('.'), // Convert back to original fieldName
        query,
        limit: 12,
      },
    })

    setSuggestions(result.data?.getFieldSuggestion || [])
  }

  // String type field
  if (field.type === 'string') {
    // Use autocomplete for fields with isAutoComplete flag
    if (field.isAutoComplete) {
      return (
        <FormItem>
          {field.label && (
            <label htmlFor={`profileInfo.${field.fieldName}`} className={styles.fieldLabel}>
              {field.label}
            </label>
          )}
          <Controller
            name={`profileInfo.${field.fieldName}`}
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <AutocompleteInput
                placeholder={field.label || ''}
                value={value as string}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={() => setFocused(true)}
                isInvalid={!!fieldError?.message}
                disabled={disabled}
                theme={disabled ? 'grey' : undefined}
                inputSize="extra_large"
                suggestions={suggestions}
                onInputChange={handleGetSuggestions}
              />
            )}
          />
          {fieldError?.message && (
            <ErrorMessage>{String(fieldError.message)}</ErrorMessage>
          )}
        </FormItem>
      )
    }

    // Regular string input
    return (
      <FormItem>
        {field.label && (
          <label htmlFor={`profileInfo.${field.fieldName}`} className={styles.fieldLabel}>
            {field.label}
          </label>
        )}
        <Controller
          name={`profileInfo.${field.fieldName}`}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <Input
              placeholder={field.label || ''}
              value={value as string}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              onFocus={() => setFocused(true)}
              isInvalid={!!fieldError?.message}
              disabled={disabled}
              theme={disabled ? 'grey' : undefined}
              inputSize="extra_large"
            />
          )}
        />
        {fieldError?.message && (
          <ErrorMessage>{String(fieldError.message)}</ErrorMessage>
        )}
      </FormItem>
    )
  }

  // Number type field
  if (field.type === 'number') {
    return (
      <FormItem>
        {field.label && (
          <label htmlFor={`profileInfo.${field.fieldName}`} className={styles.fieldLabel}>
            {field.label}
          </label>
        )}
        <Controller
          name={`profileInfo.${field.fieldName}`}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <Input
              type="number"
              placeholder={field.label || ''}
              value={value as string}
              onChange={(e) => {
                const numValue = e.target.value === '' ? '' : Number(e.target.value)
                onChange(numValue)
              }}
              onBlur={onBlur}
              onFocus={() => setFocused(true)}
              isInvalid={!!fieldError?.message}
              disabled={disabled}
              theme={disabled ? 'grey' : undefined}
              inputSize="extra_large"
            />
          )}
        />
        {fieldError?.message && (
          <ErrorMessage>{String(fieldError.message)}</ErrorMessage>
        )}
      </FormItem>
    )
  }

  // Email type field
  if (field.type === 'email') {
    const isEmailInvalid =
      !!fieldError?.message ||
      (!isCorrectEmail && fieldValue !== originalEmail)

    return (
      <FormItem>
        {field.label && (
          <label htmlFor={`profileInfo.${field.fieldName}`} className={styles.fieldLabel}>
            {field.label}
          </label>
        )}
        <Controller
          name={`profileInfo.${field.fieldName}`}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <Input
              type="email"
              placeholder={field.label || ''}
              value={value as string}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              onFocus={() => setFocused(true)}
              isInvalid={isEmailInvalid}
              disabled={disabled}
              theme={disabled ? 'grey' : undefined}
              inputSize="extra_large"
            />
          )}
        />
        {fieldError?.message && (
          <ErrorMessage>{String(fieldError.message)}</ErrorMessage>
        )}
        {!isCorrectEmail && fieldValue !== originalEmail && (
          <ErrorMessage>Email уже используется</ErrorMessage>
        )}
      </FormItem>
    )
  }

  // Select type field
  if (field.type === 'select') {
    const options =
      field.fixedAnswers?.map((answer) => ({
        name: answer?.name || '',
        value: answer?.value || '',
      })) || []

    // MultiSelect для полей с isMultiSelect
    if (field.isMultiSelect) {
      return (
        <FormItem>
          {field.label && (
            <label htmlFor={`profileInfo.${field.fieldName}`} className={styles.fieldLabel}>
              {field.label}
            </label>
          )}
          <Controller
            name={`profileInfo.${field.fieldName}`}
            control={control}
            render={({ field: { value, onChange } }) => {
              // Значение хранится как JSON строка массива
              const parsedValue: string[] = (() => {
                if (Array.isArray(value)) return value.map(String)
                if (typeof value === 'string' && value) {
                  try {
                    const parsed = JSON.parse(value)
                    return Array.isArray(parsed) ? parsed.map(String) : []
                  }
                  catch {
                    return []
                  }
                }
                return []
              })()

              return (
                <MultiSelect
                  options={options}
                  placeholder={field.label}
                  value={parsedValue}
                  onChange={(newValues) => onChange(JSON.stringify(newValues))}
                  theme={disabled ? 'grey' : undefined}
                  inputSize="extra_large"
                  disabled={disabled}
                  isInvalid={!!fieldError?.message}
                />
              )
            }}
          />
          {fieldError?.message && (
            <ErrorMessage>{String(fieldError.message)}</ErrorMessage>
          )}
        </FormItem>
      )
    }

    // Обычный Select
    return (
      <FormItem>
        {field.label && (
          <label htmlFor={`profileInfo.${field.fieldName}`} className={styles.fieldLabel}>
            {field.label}
          </label>
        )}
        <Controller
          name={`profileInfo.${field.fieldName}`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              options={options}
              placeholder={field.label}
              value={value as string}
              handleOption={onChange}
              theme={disabled ? 'grey' : undefined}
              inputSize="extra_large"
              disabled={disabled}
              isInvalid={!!fieldError?.message}
            />
          )}
        />
        {fieldError?.message && (
          <ErrorMessage>{String(fieldError.message)}</ErrorMessage>
        )}
      </FormItem>
    )
  }

  // Date type field
  if (field.type === 'date') {
    return (
      <FormItem>
        {field.label && (
          <label htmlFor={`profileInfo.${field.fieldName}`} className={styles.fieldLabel}>
            {field.label}
          </label>
        )}
        <Controller
          name={`profileInfo.${field.fieldName}`}
          control={control}
          render={({ field: { value, onChange } }) => {
            // Parse string value to Date object for InputDate component
            let dateValue: Date | null = null
            if (value) {
              const parsed = new Date(value as string)
              if (!isNaN(parsed.getTime())) {
                dateValue = parsed
              }
            }
            
            return (
              <InputDate
                value={dateValue}
                onChange={(date) => {
                  // Convert Date back to ISO string for form storage
                  onChange(date ? date.toISOString() : '')
                }}
                placeholder={`${field.label}`}
                theme={disabled ? 'grey' : undefined}
                inputSize="extra_large"
                disabled={disabled}
              />
            )
          }}
        />
        {fieldError?.message && (
          <ErrorMessage>{String(fieldError.message)}</ErrorMessage>
        )}
      </FormItem>
    )
  }

  // Boolean type field (checkbox)
  if (field.type === 'boolean') {
    return (
      <FormItem>
        <Controller
          name={`profileInfo.${field.fieldName}`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
                disabled={disabled}
                style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
              />
              <span>{field.label}</span>
            </label>
          )}
        />
        {fieldError?.message && (
          <ErrorMessage>{String(fieldError.message)}</ErrorMessage>
        )}
      </FormItem>
    )
  }

  return null
}
